import { Injectable, inject } from '@angular/core';
import { HttpClient, HttpParams } from '@angular/common/http';
import { Observable } from 'rxjs';
import { environment } from '../../environments/environment';

/** Mirrors BulkScrapRequestStatus.AllStatuses in the API (ClearScrap.Common). */
export const BULK_SCRAP_STATUSES = [
  'Submitted',
  'Contacted',
  'UnderReview',
  'Quoted',
  'Scheduled',
  'Completed',
  'Cancelled',
] as const;
export type BulkScrapStatus = (typeof BULK_SCRAP_STATUSES)[number];

/** Mirrors BulkScrapRequestSource.AllSources in the API; the API rejects anything else. */
export const BULK_SCRAP_SOURCES = [
  'iOS',
  'Android',
  'WebApp',
  'ControlCenter',
  'WhatsApp',
  'MobileApp',
] as const;
export type BulkScrapSource = (typeof BULK_SCRAP_SOURCES)[number];

export interface BulkScrapCategorySelectionDto {
  categoryId?: string;
  categoryName: string;
  estimatedQuantity?: string;
}

export interface BulkScrapStatusLogDto {
  status: BulkScrapStatus;
  remark?: string;
  updatedBy: string;
  updatedAt: string;
}

export interface BulkScrapRequestDto {
  id: string;
  customerId?: string;
  organizationName: string;
  contactPerson: string;
  contactPhone: string;
  contactEmail?: string;
  address: string;
  locationName?: string;
  pincode?: string;
  latitude?: number;
  longitude?: number;
  categories: BulkScrapCategorySelectionDto[];
  estimatedVolume: string;
  estimatedVolumeUnit?: string;
  preferredPickupDate?: string;
  preferredTimeSlot?: string;
  frequency?: string;
  additionalNotes?: string;
  status: BulkScrapStatus;
  source: BulkScrapSource;
  photoUrls: string[];
  createdAt: string;
  updatedAt?: string;
  statusHistory?: BulkScrapStatusLogDto[];
}

export interface BulkScrapPaginatedResponse {
  items: BulkScrapRequestDto[];
  totalCount: number;
  pageIndex: number;
  pageSize: number;
}

export interface BulkScrapStatusUpdate {
  status: BulkScrapStatus;
  remark?: string;
}

/** Max remark length enforced by BulkScrapStatusUpdateValidator in the API. */
export const BULK_SCRAP_REMARK_MAX_LENGTH = 500;

@Injectable({ providedIn: 'root' })
export class BulkScrapRequestService {
  private http = inject(HttpClient);
  private readonly baseUrl = `${environment.apiConfig.uri}/api/BulkScrapRequest`;

  getList(
    pageIndex = 0,
    pageSize = 20,
    status?: BulkScrapStatus
  ): Observable<BulkScrapPaginatedResponse> {
    let params = new HttpParams().set('pageIndex', pageIndex).set('pageSize', pageSize);
    if (status) {
      params = params.set('status', status);
    }
    return this.http.get<BulkScrapPaginatedResponse>(`${this.baseUrl}/list`, { params });
  }

  getById(id: string): Observable<BulkScrapRequestDto> {
    return this.http.get<BulkScrapRequestDto>(`${this.baseUrl}/${encodeURIComponent(id)}`);
  }

  updateStatus(id: string, dto: BulkScrapStatusUpdate): Observable<BulkScrapRequestDto> {
    return this.http.patch<BulkScrapRequestDto>(
      `${this.baseUrl}/${encodeURIComponent(id)}/status`,
      dto
    );
  }
}
